import { Button } from '@/components/ui/button';
import { useStartTimer, useStopTimer, useTaskTimeEntries } from '@/hooks/use-time-entries';
import { useBoard } from '@/hooks/use-boards';
import { useAuthStore } from '@/stores/auth';
import { useTimerStore } from '@/stores/timer';
import { useExtensionsStore } from '@/stores/extensions';
import type { TaskDetail, TimeEntryItem } from '@hygge/shared';
import { Clock, Play, Plus, Square } from 'lucide-react';
import { useState } from 'react';
import { EditEntryDialog } from './edit-entry-dialog';
import { ManualEntryForm } from './manual-entry-form';
import { timerControl } from '../lib/timer-visibility';
import { canEditEntry, entryLockMessage } from '../lib/entry-lock';
import { viewerTimeZone } from '@/lib/timezone';
import { entryStartWall, formatEntryDate } from '../lib/wall-clock';
import { useEmployeeNameFormatter } from '@/lib/employee-name';

interface TimeTrackingSectionProps {
  task: TaskDetail;
  boardId: string;
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h === 0 && m === 0) return '0m';
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

export function TimeTrackingSection({ task, boardId }: TimeTrackingSectionProps) {
  const isTimeTrackerEnabled = useExtensionsStore((s) => s.isExtensionEnabled('time-tracker'));
  const { data: board } = useBoard(isTimeTrackerEnabled ? boardId : undefined);
  const projectId = board?.projectId ?? null;
  const { data: timeData } = useTaskTimeEntries(isTimeTrackerEnabled ? task.id : undefined);
  const timerStore = useTimerStore();
  // This list is the whole task's, not this reader's: `GET /time-entries/task/:id`
  // filters by task alone. Which of those rows may be opened is the server's
  // answer, not a guess from the owner id — a manager of the entry's project and
  // HR may edit anyone's, and neither project membership nor the monthly cutoff
  // is knowable here. `canEdit` carries that answer; `myEmployeeId` now decides
  // only whether the row needs a name on it. [HW-864]
  const myEmployeeId = useAuthStore((s) => s.user?.employee?.id);
  const formatName = useEmployeeNameFormatter();
  // Resolved once for the list below rather than once per legacy row. [HW-916]
  const viewerZone = viewerTimeZone();
  const startTimer = useStartTimer();
  const stopTimer = useStopTimer();
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntryItem | null>(null);

  if (!isTimeTrackerEnabled || !projectId) return null;

  const totalSeconds = timeData?.totalSeconds ?? 0;
  const entries = timeData?.entries ?? [];
  const isTimerRunningForTask = timerStore.isRunning && timerStore.taskId === task.id;
  // Asked of THIS task's timer, not of any timer: a timer running on another
  // task must not put a stop button on this one. [HW-871]
  const control = timerControl(isTimerRunningForTask);

  const handleToggleTimer = () => {
    if (isTimerRunningForTask) {
      stopTimer.mutate();
    } else {
      startTimer.mutate({ taskId: task.id, projectId });
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" />
          Time Tracked
          <span className="text-foreground font-mono normal-case ml-1">
            {formatDuration(totalSeconds)}
          </span>
        </h3>
        <div className="flex items-center gap-1">
          {/* Only the stop half survives: starting a timer is withdrawn from the
              UI, and this toggle was one of its two ways in. A task whose timer
              is running keeps the control that ends it. Logging time by hand,
              below, is unaffected. One button with a conditional icon, so the
              two states cannot drift apart when the flag comes back. [HW-871] */}
          {control !== 'none' && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={handleToggleTimer}
              disabled={startTimer.isPending || stopTimer.isPending}
            >
              {control === 'stop' ? (
                <Square className="h-3 w-3 text-red-500" />
              ) : (
                <Play className="h-3 w-3 text-primary" />
              )}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setShowManualEntry(true)}
          >
            <Plus className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {entries.length > 0 && (
        <div className="space-y-1">
          {/* An entry logged here is corrected here: the same dialog the Time
              Tracker's own timesheet opens, rather than a read-only line that
              sends the reader looking for the screen that owns it. */}
          {entries.slice(0, 5).map((entry) => {
            const mine = entry.employeeId === myEmployeeId;
            const label = (
              <>
                <span className="flex min-w-0 items-center gap-1.5">
                  {/* Whose hours these are, on every row that is not yours: while
                      the row was read-only its owner did not matter, but a row
                      that opens an editor has to say whose eight hours are about
                      to change. [HW-864] */}
                  {!mine && (
                    <span className="shrink-0 text-foreground">{formatName(entry.employee)}</span>
                  )}
                  <span className="text-muted-foreground truncate">
                    {entry.description ||
                      formatEntryDate(entryStartWall(entry, viewerZone), { month: 'short', day: 'numeric' })}
                  </span>
                </span>
                <span className="font-mono text-muted-foreground shrink-0">{formatDuration(entry.duration)}</span>
              </>
            );

            return canEditEntry(entry) ? (
              <button
                key={entry.id}
                type="button"
                onClick={() => setEditingEntry(entry)}
                className="flex w-[calc(100%+0.5rem)] items-center justify-between gap-2 -mx-1 px-1 py-0.5 rounded-md text-xs text-left cursor-pointer hover:bg-accent/50 transition-colors"
              >
                {label}
              </button>
            ) : (
              <div
                key={entry.id}
                title={entryLockMessage(entry) ?? undefined}
                className="flex items-center justify-between gap-2 py-0.5 text-xs"
              >
                {label}
              </div>
            );
          })}
          {entries.length > 5 && (
            <p className="text-[11px] text-muted-foreground">+{entries.length - 5} more</p>
          )}
        </div>
      )}

      {showManualEntry && (
        <ManualEntryForm
          onClose={() => setShowManualEntry(false)}
          defaultProjectId={projectId}
          projectName={board?.projectName ?? null}
          defaultTaskId={task.id}
          taskLabel={task.key}
        />
      )}

      {editingEntry && (
        <EditEntryDialog entry={editingEntry} onClose={() => setEditingEntry(null)} />
      )}
    </>
  );
}
